import { useState } from 'react';
import { TextField } from '../../components/ui/Field';
import { toDecimal, toNumber } from './numbers';

interface CostValues {
  kmPerYear: string;
  consumption: string;
  price: string;
  insurance: string;
  maintenance: string;
}

const EMPTY: CostValues = {
  kmPerYear: '',
  consumption: '',
  price: '',
  insurance: '',
  maintenance: '',
};

const euros = (value: number, fractions = 0) =>
  value.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: fractions,
    maximumFractionDigits: fractions,
  });

export interface ConsumptionSuggestion {
  /** Per 100 km, in the unit of the drive (litres or kWh). */
  value: number;
  /** Worked out from the CO₂ value, not reported. */
  estimated: boolean;
  /** The test the value comes from. */
  cycle: 'wltp' | 'nedc';
  /** A pick by hand: the value also replaces one typed in. */
  replace: boolean;
}

/** ICCT: real-world CO₂ emission values of vehicles in Europe (January 2024), with the NEDC gap. */
const NEDC_GAP_URL =
  'https://theicct.org/publication/real-world-co2-emission-values-vehicles-europe-jan24/';

const decimal = (value: number) => value.toLocaleString('de-DE');

/** The European Commission's first report on real-world fuel consumption (March 2024). */
const REAL_WORLD_GAP_URL =
  'https://climate.ec.europa.eu/news-other-reads/news/first-commission-report-real-world-co2-emissions-cars-and-vans-using-data-board-fuel-consumption-2024-03-18_en';

interface Parsed {
  value: number | null;
  error?: string;
}

/** A typed value checked against a plausible upper limit; empty means „not given“. */
function parse(
  raw: string,
  read: (value: string) => number | null,
  max: number,
  tooHigh: string,
): Parsed {
  // Empty, or only the first keystroke of „,35“.
  if (/^\s*[.,]?\s*$/.test(raw)) return { value: null };
  const value = read(raw);
  if (value === null) return { value: null, error: 'Bitte gib nur eine Zahl ein.' };
  if (value > max) return { value: null, error: tooHigh };
  return { value };
}

/**
 * Rough running costs from the buyer's own figures: tax (from the calculator
 * above), energy, insurance and a reserve for workshop costs. KaufCheck has no
 * price data of its own; only the consumption of an engine picked above is
 * prefilled, from the EU data.
 */
export function RunningCosts({
  taxAnnualEur,
  taxNote = null,
  electric,
  suggestion = null,
}: {
  /** Annual Kfz-Steuer from the calculator; `null` while it is unknown. */
  taxAnnualEur: number | null;
  /** E.g. when a tax exemption ends. */
  taxNote?: string | null;
  electric: boolean;
  /** The consumption of an engine picked above; a new pick replaces the field. */
  suggestion?: ConsumptionSuggestion | null;
}) {
  const [values, setValues] = useState(EMPTY);
  const set = (key: keyof CostValues) => (event: { target: { value: string } }) =>
    setValues((current) => ({ ...current, [key]: event.target.value }));
  // Consumption and price change their unit with the drive, so they start empty again.
  const [unitsElectric, setUnitsElectric] = useState(electric);
  if (unitsElectric !== electric) {
    setUnitsElectric(electric);
    setValues((current) => ({ ...current, consumption: '', price: '' }));
  }
  const [suggested, setSuggested] = useState<ConsumptionSuggestion | null>(null);
  const fromPick = suggested !== null && values.consumption === decimal(suggested.value);
  if (suggestion !== suggested) {
    setSuggested(suggestion);
    // A value of the previous pick goes; one typed in stays, unless an engine is picked by hand.
    const ours = fromPick || values.consumption.trim() === '';
    if (suggestion ? ours || suggestion.replace : fromPick)
      setValues((current) => ({
        ...current,
        consumption: suggestion ? decimal(suggestion.value) : '',
      }));
  }

  const km = parse(values.kmPerYear, toNumber, 300_000, 'Bitte prüfe die Fahrleistung.');
  const consumption = parse(
    values.consumption,
    toDecimal,
    electric ? 100 : 50,
    'Bitte prüfe den Verbrauch.',
  );
  const price = parse(
    values.price,
    toDecimal,
    // Hydrogen costs well over 10 € per kg.
    electric ? 20 : 5,
    electric
      ? 'Bitte prüfe den Preis, z. B. 0,35 je kWh.'
      : 'Bitte prüfe den Preis, z. B. 1,79 je Liter.',
  );
  const insurance = parse(values.insurance, toNumber, 100_000, 'Bitte prüfe den Jahresbeitrag.');
  const maintenance = parse(values.maintenance, toNumber, 100_000, 'Bitte prüfe den Betrag.');
  const energy =
    km.value !== null && consumption.value !== null && price.value !== null
      ? (km.value / 100) * consumption.value * price.value
      : null;

  // Rounded per row, so that the rows add up to the total shown.
  const round = (value: number | null) => (value === null ? null : Math.round(value));
  const parts = [
    { label: 'Kfz-Steuer', value: round(taxAnnualEur) },
    { label: electric ? 'Strom' : 'Kraftstoff', value: round(energy) },
    { label: 'Versicherung', value: round(insurance.value) },
    { label: 'Werkstatt und Wartung', value: round(maintenance.value) },
  ];
  const known = parts.filter(
    (part): part is { label: string; value: number } => part.value !== null,
  );
  const missing = parts.filter((part) => part.value === null).map((part) => part.label);
  const total = known.reduce((sum, part) => sum + part.value, 0);
  // The tax alone is no estimate of the running costs.
  const started = energy !== null || insurance.value !== null || maintenance.value !== null;

  return (
    <div className="tax-calculator">
      <form className="tax-form" onSubmit={(event) => event.preventDefault()} noValidate>
        <TextField
          label="Fahrleistung in km pro Jahr"
          inputMode="numeric"
          error={km.error}
          value={values.kmPerYear}
          onChange={set('kmPerYear')}
        />
        <TextField
          label={electric ? 'Verbrauch in kWh je 100 km' : 'Verbrauch in Litern je 100 km'}
          hint={
            fromPick && electric ? (
              'Normverbrauch (WLTP) laut EU-Daten, mit Ladeverlusten.'
            ) : fromPick ? (
              <>
                {suggested?.estimated
                  ? `Aus dem CO₂-Wert des Motors (${suggested.cycle === 'wltp' ? 'WLTP' : 'NEFZ'}) geschätzt.`
                  : 'Normverbrauch (WLTP) laut EU-Daten.'}{' '}
                {suggested?.cycle === 'wltp' ? (
                  <>
                    Im Alltag liegt der Verbrauch von Benzinern und Dieseln laut{' '}
                    <a href={REAL_WORLD_GAP_URL} target="_blank" rel="noopener noreferrer">
                      EU-Kommission
                    </a>{' '}
                    rund ein Fünftel höher.
                  </>
                ) : (
                  <>
                    Beim alten NEFZ-Test lag der Verbrauch im Alltag laut{' '}
                    <a href={NEDC_GAP_URL} target="_blank" rel="noopener noreferrer">
                      ICCT
                    </a>{' '}
                    rund ein Drittel höher.
                  </>
                )}
              </>
            ) : electric ? (
              'WLTP-Wert aus Inserat oder Datenblatt (mit Ladeverlusten); Bordcomputer-Werte ohne Ladeverluste. Wasserstoff: kg je 100 km.'
            ) : (
              'Laut Inserat oder Hersteller; im Alltag meist etwas mehr. Erdgas (CNG): kg je 100 km und Preis je kg.'
            )
          }
          inputMode="decimal"
          error={consumption.error}
          value={values.consumption}
          onChange={set('consumption')}
        />
        <TextField
          label={electric ? 'Strompreis in € je kWh' : 'Kraftstoffpreis in € je Liter'}
          hint={
            electric
              ? 'Dein Haushaltsstrom- oder Ladetarif; Wasserstoff: Preis je kg'
              : 'Aktueller Preis an der Tankstelle oder in einer Spritpreis-App'
          }
          inputMode="decimal"
          error={price.error}
          value={values.price}
          onChange={set('price')}
        />
        <TextField
          label="Versicherung in € pro Jahr"
          hint={
            <>
              Angebot deines Versicherers – abhängig unter anderem von der{' '}
              <a href="#typklasse">Typklasse</a>, der Region und deiner Schadenfreiheitsklasse
            </>
          }
          inputMode="decimal"
          error={insurance.error}
          value={values.insurance}
          onChange={set('insurance')}
        />
        <TextField
          label="Werkstatt und Wartung in € pro Jahr"
          hint="Deine Annahme für Inspektion, Reifen, HU/AU und Reparaturen; ältere Autos brauchen mehr Rücklage"
          inputMode="decimal"
          error={maintenance.error}
          value={values.maintenance}
          onChange={set('maintenance')}
        />
      </form>
      <section className="tax-result" aria-labelledby="costs-result-title">
        <h3 id="costs-result-title" className="tax-result__title">
          Laufende Kosten
        </h3>
        <p className={started ? 'tax-result__amount' : 'tax-result__hint'} aria-live="polite">
          {started ? (
            <>
              <span className="visually-hidden">Laufende Kosten: </span>rund {euros(total / 12)}{' '}
              <span>im Monat</span>
            </>
          ) : (
            'Trag deine Werte ein.'
          )}
        </p>
        {started && (
          <>
            <p className="tax-result__monthly">
              {euros(total)} im Jahr
              {km.value && energy !== null ? `, ${euros(total / km.value, 2)} je km` : ''}
            </p>
            <ul className="tax-result__parts">
              {known.map((part) => (
                <li key={part.label}>
                  <span>{part.label}</span>
                  <span className="tax-result__value">{euros(part.value)}</span>
                </li>
              ))}
            </ul>
            {taxNote && <p className="tax-result__hint">{taxNote}</p>}
            {missing.length > 0 && (
              <p className="tax-result__hint">Noch nicht eingerechnet: {missing.join(', ')}.</p>
            )}
            <p className="tax-result__rule">
              Ohne Wertverlust, Stellplatz, Parken und Pflege. Bei jüngeren Autos ist der
              Wertverlust oft der größte Posten.
            </p>
          </>
        )}
      </section>
    </div>
  );
}
